import { useEffect, useState, type FormEvent } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';

const navItems = [
  { to: '/', label: '首页' },
  { to: '/movies', label: '电影' },
  { to: '/tvshows', label: '电视剧' },
  { to: '/news', label: '影视资讯' },
  { to: '/about', label: '关于' },
];

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname, location.search]);

  const handleSearch = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
    setSearchOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200 bg-background-50/95 backdrop-blur-md">
      <div className="flex h-16 w-full items-center gap-4 px-4 md:h-[72px] md:px-8">
        <Link to="/" className="group flex shrink-0 items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
            <i className="ri-movie-2-line text-[19px]"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-[17px] font-bold tracking-tight text-foreground-950">
              玩偶姐姐
            </span>
            <span className="mt-1 font-label text-[10px] uppercase tracking-[0.18em] text-foreground-500">
              Film &amp; Series
            </span>
          </span>
        </Link>

        <nav className="ml-6 hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) =>
                `rounded-md px-3.5 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-primary-50 font-medium text-primary-700'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form
            onSubmit={handleSearch}
            className={`hidden items-center rounded-full border border-background-300 bg-background-100 pl-3 transition-all md:flex ${
              searchOpen ? 'w-64' : 'w-40'
            }`}
          >
            <i className="ri-search-line text-[15px] text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              onFocus={() => setSearchOpen(true)}
              placeholder="搜索影视或资讯"
              aria-label="站内搜索"
              className="w-full bg-transparent px-2 py-2 text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              aria-label="提交搜索"
              className="mr-1 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-arrow-right-line text-[14px]"></i>
            </button>
          </form>

          <Link
            to="/search"
            aria-label="搜索"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 md:hidden"
          >
            <i className="ri-search-line text-[17px]"></i>
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="打开导航菜单"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 transition-colors hover:border-primary-300 md:hidden"
          >
            <i className={mobileOpen ? 'ri-close-line text-[19px]' : 'ri-menu-line text-[19px]'}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="animate-fade-in border-t border-background-200 bg-background-50 px-4 pb-5 pt-3 md:hidden">
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  `flex items-center justify-between rounded-md px-3 py-3 text-sm ${
                    isActive
                      ? 'bg-primary-50 font-medium text-primary-700'
                      : 'text-foreground-700'
                  }`
                }
              >
                {item.label}
                <i className="ri-arrow-right-s-line text-foreground-400"></i>
              </NavLink>
            ))}
          </nav>
          <form onSubmit={handleSearch} className="mt-3 flex items-center rounded-full border border-background-300 bg-background-100 pl-3">
            <i className="ri-search-line text-[15px] text-foreground-500"></i>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              placeholder="搜索影视或资讯"
              aria-label="站内搜索"
              className="w-full bg-transparent px-2 py-2.5 text-sm outline-none placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="mr-1 flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-primary-500 text-background-50"
              aria-label="提交搜索"
            >
              <i className="ri-arrow-right-line text-[14px]"></i>
            </button>
          </form>
        </div>
      )}
    </header>
  );
}